import React from 'react';
import { Shield, Info, Lock, Users, DollarSign, FileText, AlertTriangle, RefreshCw, Phone } from 'lucide-react';
import { useI18n } from '@/i18n';

const PrivacyPolicyPage: React.FC = () => {
  const { locale } = useI18n();

  if (locale === 'zh-CN') {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-primary py-16 text-white">
          <div className="container mx-auto px-4">
            <div className="mb-4 flex items-center justify-center">
              <Shield className="mr-4 h-12 w-12" />
              <h1 className="text-4xl font-bold md:text-5xl">隐私政策</h1>
            </div>
            <p className="mx-auto max-w-3xl text-center text-gray-200">生效日期：2026 年 4 月 23 日</p>
            <p className="mx-auto mt-2 max-w-3xl text-center text-gray-200">最后更新：2026 年 4 月 23 日</p>
          </div>
        </div>

        <div className="container mx-auto max-w-5xl px-4 py-12">
          <section className="mb-8 rounded-lg bg-white p-8 shadow-md">
            <div className="mb-4 flex items-start">
              <Info className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-primary" />
              <div className="text-gray-700">
                <h2 className="mb-4 text-2xl font-bold text-primary">1. 简介</h2>
                <div className="space-y-4">
                  <p>
                    欢迎使用 <strong>Tutor Founder</strong>。我们是在马来西亚营运的导师与学生配对平台，致力于依照适用法律与规范，谨慎处理并保护你的个人资料。
                  </p>
                  <p>
                    本隐私政策说明我们在你使用网站与服务时，如何收集、使用、披露、保存与保护你的个人资料。使用本平台即表示你已阅读、理解并同意本政策及相关服务条款。
                  </p>
                  <p>
                    本政策遵循马来西亚《2010 年个人资料保护法》（PDPA）以及其他相关法规与消费者保护要求。
                  </p>
                  <p className="font-semibold text-primary">若你不同意本政策内容，请不要继续访问或使用我们的服务。</p>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-8 rounded-lg bg-white p-8 shadow-md">
            <div className="mb-4 flex items-start">
              <Lock className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-primary" />
              <div className="w-full text-gray-700">
                <h2 className="mb-4 text-2xl font-bold text-primary">2. 我们收集的资料</h2>
                <p className="mb-4">为提供与优化服务，我们可能会收集以下资料：</p>
                <div className="ml-4 space-y-5">
                  <div>
                    <h3 className="mb-3 text-xl font-semibold text-secondary">2.1 身份资料</h3>
                    <ul className="ml-6 list-disc space-y-2">
                      <li>姓名、出生日期、性别、国籍、头像照片</li>
                      <li>身份证、护照或其他身份证明资料，用于身份核验</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="mb-3 text-xl font-semibold text-secondary">2.2 联络资料</h3>
                    <ul className="ml-6 list-disc space-y-2">
                      <li>电邮地址、手机号码、住址与偏好联络方式</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="mb-3 text-xl font-semibold text-secondary">2.3 教育与专业资料</h3>
                    <ul className="ml-6 list-disc space-y-2">
                      <li>学历、证书、教学经验、教授科目、授课地区与可授课时间</li>
                      <li>导师提交的成绩单、证明文件与相关背景资料</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="mb-3 text-xl font-semibold text-secondary">2.4 付款与财务资料</h3>
                    <ul className="ml-6 list-disc space-y-2">
                      <li>导师收款所需的银行户口资料</li>
                      <li>付款纪录、交易资料、账单与发票资讯</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="mb-3 text-xl font-semibold text-secondary">2.5 使用与技术资料</h3>
                    <ul className="ml-6 list-disc space-y-2">
                      <li>IP 位址、装置资讯、浏览器类型与版本</li>
                      <li>访问页面、停留时间、Cookies、搜索纪录与偏好设定</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="mb-3 text-xl font-semibold text-secondary">2.6 沟通与反馈资料</h3>
                    <ul className="ml-6 list-disc space-y-2">
                      <li>平台讯息、WhatsApp 沟通纪录、课时验证讯息与时间戳</li>
                      <li>客服沟通、评价、评分、调查回覆与反馈内容</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-8 rounded-lg bg-white p-8 shadow-md">
            <div className="mb-4 flex items-start">
              <Users className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-primary" />
              <div className="w-full text-gray-700">
                <h2 className="mb-4 text-2xl font-bold text-primary">3. 我们如何使用资料</h2>
                <div className="ml-4 space-y-5">
                  <div>
                    <h3 className="mb-3 text-xl font-semibold text-secondary">3.1 身份验证与安全</h3>
                    <ul className="ml-6 list-disc space-y-2">
                      <li>验证导师与家长/学生身份，防止欺诈、滥用与未授权访问</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="mb-3 text-xl font-semibold text-secondary">3.2 提供配对与服务</h3>
                    <ul className="ml-6 list-disc space-y-2">
                      <li>进行导师与学生配对、安排沟通、处理预约与课表管理</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="mb-3 text-xl font-semibold text-secondary">3.3 付款与结算</h3>
                    <ul className="ml-6 list-disc space-y-2">
                      <li>处理家长/学生预付的学费、托管款项及导师拨款</li>
                      <li>管理平台在第 1-2 个月的 25% 佣金以及第 3 个月起 0% 佣金安排</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="mb-3 text-xl font-semibold text-secondary">3.4 WhatsApp 课时验证</h3>
                    <ul className="ml-6 list-disc space-y-2">
                      <li>在每次课程后发送验证提醒，并在未回覆时自动更新课时状态</li>
                      <li>用于付款核对、争议处理与审计纪录保存</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="mb-3 text-xl font-semibold text-secondary">3.5 平台改善与沟通</h3>
                    <ul className="ml-6 list-disc space-y-2">
                      <li>分析使用行为、改进平台体验、开发新功能、发送通知与行销讯息（在取得同意时）</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="mb-3 text-xl font-semibold text-secondary">3.6 合规与平台保护</h3>
                    <ul className="ml-6 list-disc space-y-2">
                      <li>执行平台规则、防止绕过平台付款、回应监管或法律要求</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-8 rounded-lg bg-white p-8 shadow-md">
            <div className="mb-4 flex items-start">
              <Users className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-primary" />
              <div className="w-full text-gray-700">
                <h2 className="mb-4 text-2xl font-bold text-primary">4. 个人资料共享</h2>
                <p className="mb-4">我们不会为了第三方营销而出售、出租或交易你的个人资料，但在以下情况下可能会共享：</p>
                <ul className="ml-6 list-disc space-y-2">
                  <li>导师与家长/学生成功配对后，分享必要的联络与档案资料以便安排课程</li>
                  <li>与支付处理方、银行或金融机构共享必要的交易资料</li>
                  <li>与代表我们提供服务的合作伙伴或服务供应商共享必要资料</li>
                  <li>为遵守法律、监管要求、法院命令或保护平台与用户权益而披露</li>
                  <li>在企业并购、重组或资产转让时，于合法范围内移转资料</li>
                  <li>在你明确同意的情况下，与指定第三方共享</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8 rounded-lg bg-white p-8 shadow-md">
            <div className="mb-4 flex items-start">
              <DollarSign className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-primary" />
              <div className="w-full text-gray-700">
                <h2 className="mb-4 text-2xl font-bold text-primary">5. 付款模式与付款条款</h2>
                <ul className="ml-6 list-disc space-y-2">
                  <li>家长/学生通过平台预约导师时，需要预付一个月的学费</li>
                  <li>款项会由 Tutor Founder 托管，并依规则于每月底拨款给导师</li>
                  <li>第 1-2 个月平台收取 25% 行政佣金；第 3 个月起由双方直接付款，平台佣金为 0%</li>
                  <li>平台会保留付款纪录，以满足会计、税务与监管要求</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8 rounded-lg bg-white p-8 shadow-md">
            <div className="mb-4 flex items-start">
              <FileText className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-primary" />
              <div className="w-full text-gray-700">
                <h2 className="mb-4 text-2xl font-bold text-primary">6. 排课、费用与取消政策</h2>
                <ul className="ml-6 list-disc space-y-2">
                  <li>月费依据当月约定课次数量计算，即使因假期、病假或缺席导致安排调整，已约定月费原则上不变</li>
                  <li>所有课程验证与安排沟通会通过官方 WhatsApp 渠道处理</li>
                  <li>学生因病或个人原因缺课时，相关课时可能转为补课额度而非直接退款</li>
                  <li>导师或家长/学生需要取消或更改安排时，应尽早通知平台与对方</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8 rounded-lg bg-white p-8 shadow-md">
            <div className="mb-4 flex items-start">
              <AlertTriangle className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-primary" />
              <div className="w-full text-gray-700">
                <h2 className="mb-4 text-2xl font-bold text-primary">7. 免责声明与责任限制</h2>
                <ul className="ml-6 list-disc space-y-2">
                  <li>Tutor Founder 的角色是中介平台，本身不直接提供补习服务</li>
                  <li>平台不对家长、学生或导师之间的现实互动、争议、学习成果或安全事故承担直接责任</li>
                  <li>即使平台进行身份验证，用户仍应自行审慎判断并承担相关风险</li>
                  <li>在法律允许范围内，平台不对间接、附带、特殊或惩罚性损害负责</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8 rounded-lg bg-white p-8 shadow-md">
            <div className="mb-4 flex items-start">
              <AlertTriangle className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-primary" />
              <div className="w-full text-gray-700">
                <h2 className="mb-4 text-2xl font-bold text-primary">8. 用户义务</h2>
                <ul className="ml-6 list-disc space-y-2">
                  <li>不得提供虚假、不准确或误导性资料</li>
                  <li>不得分享密码、干扰平台运作、进行欺诈、骚扰或违法行为</li>
                  <li>必须妥善保管账户，并在资料变更时及时更新</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8 rounded-lg bg-white p-8 shadow-md">
            <div className="mb-4 flex items-start">
              <Lock className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-primary" />
              <div className="w-full text-gray-700">
                <h2 className="mb-4 text-2xl font-bold text-primary">9. 资料安全与保存期限</h2>
                <ul className="ml-6 list-disc space-y-2">
                  <li>我们采用行业标准的技术与管理措施保护个人资料</li>
                  <li>个人资料会在实现本政策目的所需期间内保存；财务纪录通常至少保存 7 年</li>
                  <li>网络传输与电子储存不存在绝对安全，平台会尽合理努力降低风险</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8 rounded-lg bg-white p-8 shadow-md">
            <div className="mb-4 flex items-start">
              <Shield className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-primary" />
              <div className="w-full text-gray-700">
                <h2 className="mb-4 text-2xl font-bold text-primary">10. 你在 PDPA 下的权利</h2>
                <ul className="ml-6 list-disc space-y-2">
                  <li>你可要求查阅、更正、更新，或在适用情况下删除你的个人资料</li>
                  <li>你可撤回特定同意，但这可能影响我们继续提供部分服务</li>
                  <li>如有申请，可透过官方联络方式提交，我们会按法律与合理程序处理</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8 rounded-lg bg-white p-8 shadow-md">
            <div className="mb-4 flex items-start">
              <RefreshCw className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-primary" />
              <div className="w-full text-gray-700">
                <h2 className="mb-4 text-2xl font-bold text-primary">11. 政策更新</h2>
                <p>
                  Tutor Founder 可在必要时更新本隐私政策。若有重大变更，我们会在平台上发布清楚通知；你继续使用平台，即表示接受更新后的版本。
                </p>
              </div>
            </div>
          </section>

          <section className="rounded-lg bg-primary p-8 text-white shadow-md">
            <div className="mb-4 flex items-start">
              <Phone className="mr-3 mt-1 h-6 w-6 flex-shrink-0" />
              <div>
                <h2 className="mb-4 text-2xl font-bold">联络我们</h2>
                <p className="mb-4">
                  如你对本隐私政策、个人资料处理方式或权利行使有任何疑问、意见或投诉，请通过以下方式联络我们：
                </p>
                <div className="space-y-2 text-white/90">
                  <p><strong>地址：</strong> Kuala Lumpur, Malaysia</p>
                  <p><strong>电邮：</strong> tutorfounder@gmail.com</p>
                  <p><strong>电话：</strong> +60 11-12049396</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-primary text-white py-16">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center mb-4">
            <Shield className="h-12 w-12 mr-4" />
            <h1 className="text-4xl md:text-5xl font-bold">Privacy Policy</h1>
          </div>
          <p className="text-center text-gray-200 max-w-3xl mx-auto">
            Effective Date: April 23, 2026
          </p>
          <p className="text-center text-gray-200 max-w-3xl mx-auto mt-2">
            Last Updated: April 23, 2026
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12 max-w-5xl">

        {/* Introduction */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <Info className="h-6 w-6 text-primary mr-3 mt-1 flex-shrink-0" />
            <div>
              <h2 className="text-2xl font-bold text-primary mb-4">1. Introduction</h2>
              <div className="text-gray-700 space-y-4">
                <p>
                  Welcome to <strong>Tutor Founder</strong> ("we," "us," "our," or "the Platform"). Tutor Founder is a private tutor–student matching service operating in Malaysia. We are committed to protecting your privacy and handling your personal information with the utmost care and in accordance with applicable laws and regulations.
                </p>
                <p>
                  This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you use our website and services. By accessing or using Tutor Founder, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy and our Terms of Service.
                </p>
                <p>
                  This Privacy Policy complies with the <strong>Personal Data Protection Act 2010 (PDPA)</strong> of Malaysia and other relevant Malaysian laws and regulations, including consumer protection requirements.
                </p>
                <p className="font-semibold text-primary">
                  If you do not agree with the terms of this Privacy Policy, please do not access or use our services.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Information We Collect */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <Lock className="h-6 w-6 text-primary mr-3 mt-1 flex-shrink-0" />
            <div className="w-full">
              <h2 className="text-2xl font-bold text-primary mb-4">2. Information We Collect</h2>
              <div className="text-gray-700 space-y-4">
                <p>
                  We collect and process various types of personal information to provide and improve our services:
                </p>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">2.1 Personal Identification Information</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>Full name (as per official identification documents)</li>
                    <li>Malaysian Identity Card (IC) number or Passport details (for identity verification purposes)</li>
                    <li>Date of birth</li>
                    <li>Gender</li>
                    <li>Nationality</li>
                    <li>Photographs (profile pictures)</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">2.2 Contact Information</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>Email address</li>
                    <li>Mobile phone number</li>
                    <li>Residential address</li>
                    <li>Preferred communication methods</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">2.3 Educational and Professional Information</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>Educational qualifications and certifications</li>
                    <li>Teaching experience and expertise</li>
                    <li>Subjects taught and proficiency levels</li>
                    <li>Preferred teaching locations and availability</li>
                    <li>Academic transcripts (for tutors)</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">2.4 Financial and Payment Information</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>Bank account details (for tutors receiving payments)</li>
                    <li>Payment transaction records (bank transfer or Touch 'n Go)</li>
                    <li>Billing addresses</li>
                    <li>Payment history and invoices</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">2.5 Usage and Technical Information</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>IP address and device information</li>
                    <li>Browser type and version</li>
                    <li>Pages visited and time spent on the Platform</li>
                    <li>Cookies and similar tracking technologies</li>
                    <li>Search queries and preferences</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">2.6 Communications and Feedback</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>Messages exchanged through our Platform and via WhatsApp, etc.</li>
                    <li>WhatsApp lesson verification communications (verification prompts, auto-completion status, timestamps)</li>
                    <li>Customer support inquiries and correspondence</li>
                    <li>Reviews, ratings, and feedback</li>
                    <li>Survey responses</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How We Use Information */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <Users className="h-6 w-6 text-primary mr-3 mt-1 flex-shrink-0" />
            <div className="w-full">
              <h2 className="text-2xl font-bold text-primary mb-4">3. How We Use the Information</h2>
              <div className="text-gray-700 space-y-4">
                <p>
                  We use your personal information for the following purposes:
                </p>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">3.1 Identity Verification and Security</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>To verify the identity of tutors and parents/students using Malaysian IC numbers or passport details</li>
                    <li>To prevent fraud, unauthorized access, and misuse of our Platform</li>
                    <li>To ensure the safety and security of all users</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">3.2 Service Provision and Matching</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>To facilitate matching between tutors and students based on preferences and requirements</li>
                    <li>To enable communication between tutors and parents/students</li>
                    <li>To manage booking requests and lesson scheduling</li>
                    <li>To provide customer support and respond to inquiries</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">3.3 Payment Processing</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>To process upfront payments from parents/students for one month of tutoring lessons</li>
                    <li>To securely hold lesson fees in escrow and calculate tutor disbursements based on commission structure (25% for Months 1-2, 0% from Month 3 onward)</li>
                    <li>To maintain accurate financial records and transaction histories</li>
                    <li>To comply with financial regulations and anti-money laundering requirements</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">3.3a Lesson Verification and WhatsApp Communication</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>To send WhatsApp verification prompts to parents/students after each lesson</li>
                    <li>To automatically mark lessons as completed if no response is received within 24 hours</li>
                    <li>To process WhatsApp communication data for lesson status tracking and payment verification</li>
                    <li>To maintain records of lesson verification for dispute resolution and audit purposes</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">3.4 Platform Improvement and Analytics</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>To analyze user behavior and preferences to improve our services</li>
                    <li>To develop new features and functionalities</li>
                    <li>To optimize user experience and Platform performance</li>
                    <li>To conduct research and statistical analysis</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">3.5 Communication and Marketing</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>To send transactional emails and notifications related to your account</li>
                    <li>To provide updates about lessons, payments, and Platform changes</li>
                    <li>To send promotional materials and marketing communications (with your consent)</li>
                    <li>To conduct surveys and gather feedback</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">3.7 Platform Integrity and Non-Circumvention Monitoring</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>To monitor and prevent users from bypassing the platform to arrange direct payments during the introductory billing period (Months 1-2)</li>
                    <li>To enforce the 12-month non-circumvention rule as specified in the Terms of Service</li>
                    <li>To process reports of platform circumvention and identify violations</li>
                    <li>To protect platform integrity and ensure fair treatment of all users</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">3.8 Legal Compliance and Regulatory Requirements</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>To comply with applicable Malaysian laws and regulations</li>
                    <li>To respond to legal requests, court orders, and regulatory inquiries</li>
                    <li>To enforce our Terms of Service and other policies</li>
                    <li>To protect the rights, property, and safety of Tutor Founder and our users</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Personal Information Sharing */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <Users className="h-6 w-6 text-primary mr-3 mt-1 flex-shrink-0" />
            <div className="w-full">
              <h2 className="text-2xl font-bold text-primary mb-4">4. Personal Information Sharing</h2>
              <div className="text-gray-700 space-y-4">
                <p>
                  We respect your privacy and will not sell, rent, or trade your personal information to third parties for marketing purposes. However, we may share your personal information in the following circumstances:
                </p>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">4.1 With Tutors and Parents/Students</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>When a match is made, we share relevant contact and profile information between tutors and parents/students to facilitate lesson arrangements</li>
                    <li>Information shared is limited to what is necessary for lesson coordination and communication</li>
                    <li>WhatsApp verification data and lesson completion status are processed by Tutor Founder for payment verification</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">4.2 With Payment Processors and Financial Institutions</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>We share payment information with authorized payment processors (bank transfer providers, Touch 'n Go) to facilitate transactions</li>
                    <li>Financial institutions may receive transaction details necessary to process payments and disbursements</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">4.3 With Service Providers and Business Partners</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>We may engage third-party service providers (e.g., hosting providers, analytics services, customer support tools) who need access to personal information to perform services on our behalf</li>
                    <li>These providers are contractually obligated to protect your information and use it only for specified purposes</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">4.4 As Required by Law</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>We may disclose personal information when required to comply with Malaysian laws, regulations, legal processes, or governmental requests</li>
                    <li>We may share information to enforce our Terms of Service or to protect the rights, property, or safety of Tutor Founder, our users, or the public</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">4.5 Business Transfers</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>In the event of a merger, acquisition, reorganization, or sale of assets, your personal information may be transferred to the acquiring entity</li>
                    <li>We will notify you via email and/or prominent notice on our Platform of any such change in ownership</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">4.6 With Your Consent</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>We may share your information with third parties when you have given explicit consent for such disclosure</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Payment Terms and Payment Model */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <DollarSign className="h-6 w-6 text-primary mr-3 mt-1 flex-shrink-0" />
            <div className="w-full">
              <h2 className="text-2xl font-bold text-primary mb-4">5. Payment Terms and Payment Model</h2>
              <div className="text-gray-700 space-y-4">

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">5.1 Upfront Payment Requirement</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>Parents/students are required to pay lesson fees upfront for <strong>one full month of tutoring lessons</strong> when booking a tutor through Tutor Founder</li>
                    <li>Payment must be made via <strong>bank transfer</strong> or <strong>Touch 'n Go (TNG)</strong> before lessons commence</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">5.2 Payment Holding and Disbursement</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>Tutor Founder securely holds all lesson fees paid by parents/students in an escrow account</li>
                    <li><strong>Months 1-2:</strong> Tutor Founder deducts a 25% administrative commission and disburses 75% to tutors at the end of each month</li>
                    <li><strong>Month 3 onward:</strong> Parents/students pay tutors directly with 0% platform commission</li>
                    <li>Disbursement is subject to verification of lesson completion and compliance with our Terms of Service</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">5.3 Payment Security</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>We implement industry-standard security measures to protect payment information</li>
                    <li>Payment processors used by Tutor Founder comply with relevant financial regulations</li>
                    <li>We do not store full credit card or bank account details on our servers</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">5.4 Payment Records</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>We maintain records of all transactions for accounting, tax, and regulatory compliance purposes</li>
                    <li>Users can access their payment history through their account dashboard</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Lesson Scheduling, Fees, and Cancellations */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <FileText className="h-6 w-6 text-primary mr-3 mt-1 flex-shrink-0" />
            <div className="w-full">
              <h2 className="text-2xl font-bold text-primary mb-4">6. Lesson Scheduling, Fees, and Cancellations</h2>
              <div className="text-gray-700 space-y-4">

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">6.1 Lesson Fees and Scheduling</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>Parents/students are required to pay lesson fees upfront for one full month of tutoring</li>
                    <li>The monthly fee is based on the agreed number of lessons scheduled for that month</li>
                    <li>If lessons are reduced due to public holidays, illness, or other absences by either the student or tutor, the payment due date may be postponed, but the total monthly fees remain unchanged</li>
                    <li>If additional lessons are conducted within the same month, no extra fees will be charged</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">6.1a Lesson Verification via WhatsApp</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>All lesson verification and scheduling communications occur via Tutor Founder's official WhatsApp channels</li>
                    <li>Following each scheduled lesson, parents/students will receive a verification prompt via WhatsApp</li>
                    <li>If no response is received within 24 hours, the lesson automatically defaults to "Completed" status to prevent payment delays for tutors</li>
                    <li>We collect and process WhatsApp communication data (timestamps, message content) for lesson verification, payment processing, and dispute resolution purposes</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">6.2 Tutor Cancellations</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>Tutors may cancel assignments, but cancellations should be communicated to Tutor Founder as soon as possible</li>
                    <li>Earlier notification allows us to assist customers in finding alternative tutors</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">6.3 Parent/Student Cancellations and Makeup Credits</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>If a student misses a scheduled lesson due to illness or personal emergencies, the funds for that lesson are non-refundable and convert into a "Makeup Credit"</li>
                    <li>Makeup Credits must be fulfilled within 30 days of the original missed lesson, after which the credit expires</li>
                    <li>Any changes to the number of lessons or cancellations requested by parents/students must be confirmed with Tutor Founder by phone within 24 hours of the request</li>
                    <li>Payment for lessons already conducted cannot be refunded, but may be credited toward future lessons or makeup sessions</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Liability and Disclaimers */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <AlertTriangle className="h-6 w-6 text-primary mr-3 mt-1 flex-shrink-0" />
            <div className="w-full">
              <h2 className="text-2xl font-bold text-primary mb-4">7. Liability and Disclaimers</h2>
              <div className="text-gray-700 space-y-4">

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">7.1 Platform Role</h3>
                  <p>
                    Tutor Founder acts solely as an intermediary platform to facilitate connections between tutors and parents/students. We do not provide tutoring services directly.
                  </p>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">7.2 No Liability for User Conduct</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li><strong>Tutor Founder is not liable</strong> for unreasonable requests made by parents/students or tutors</li>
                    <li>We are not responsible for any conflicts, disputes, or incidents that may arise from physical meetings or interactions between students/parents and tutors</li>
                    <li>Users engage with each other at their own risk and are solely responsible for their conduct during lessons and all related activities</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">7.3 Verification Limitations</h3>
                  <p>
                    While we verify identity information (IC numbers, passport details), we conduct comprehensive verification checks on all users. Parents/students are encouraged to exercise due diligence when selecting tutors.
                  </p>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">7.4 Dispute Resolution</h3>
                  <p>
                    Any disputes between tutors and parents/students should be resolved directly between the parties involved. Tutor Founder may provide mediation assistance upon request but is under no obligation to do so.
                  </p>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">7.5 Limitation of Liability</h3>
                  <p>
                    To the fullest extent permitted by Malaysian law, Tutor Founder shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of our Platform or services.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* User Obligations */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <AlertTriangle className="h-6 w-6 text-primary mr-3 mt-1 flex-shrink-0" />
            <div className="w-full">
              <h2 className="text-2xl font-bold text-primary mb-4">8. User Obligations</h2>
              <div className="text-gray-700 space-y-4">
                <p>
                  By using Tutor Founder, you agree to comply with the following obligations:
                </p>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">8.1 Prohibited Activities</h3>
                  <p className="mb-2">Users must <strong>NOT</strong>:</p>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>Use any device, software, or program to interfere or attempt to interfere with the normal operation of this website or any transactions conducted on this website</li>
                    <li>Engage in any activity that imposes an unreasonable or disproportionately large load on the infrastructure of this website</li>
                    <li>Disclose their password to any third party, share a password with any third party, or use their password for any unauthorized purpose</li>
                    <li>Submit false, misleading, or inaccurate information during registration or throughout use of the Platform</li>
                    <li>Engage in fraudulent activities, harassment, or any unlawful conduct</li>
                    <li>Violate any applicable laws or regulations</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">8.2 Account Security</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>You are responsible for maintaining the confidentiality of your account credentials</li>
                    <li>You must notify us immediately of any unauthorized access to your account</li>
                    <li>You are liable for all activities conducted under your account</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">8.3 Accurate Information</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>You must provide accurate, current, and complete information when registering and using our services</li>
                    <li>You must promptly update any changes to your personal information</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Data Security and Retention */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <Lock className="h-6 w-6 text-primary mr-3 mt-1 flex-shrink-0" />
            <div className="w-full">
              <h2 className="text-2xl font-bold text-primary mb-4">9. Data Security and Retention</h2>
              <div className="text-gray-700 space-y-4">

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">9.1 Security Measures</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>We implement industry-standard technical and organizational security measures to protect your personal information against unauthorized access, disclosure, alteration, or destruction</li>
                    <li>These measures include encryption, secure server infrastructure, access controls, and regular security audits</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">9.2 Data Retention</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>We retain personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law</li>
                    <li>Financial records are retained for a minimum of seven (7) years in compliance with Malaysian tax and accounting regulations</li>
                    <li>Upon account deletion or termination, we will delete or anonymize your personal information, except where retention is legally required</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">9.3 No Absolute Security</h3>
                  <p>
                    While we strive to protect your personal information, no method of transmission over the internet or electronic storage is 100% secure.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Your Rights */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <Shield className="h-6 w-6 text-primary mr-3 mt-1 flex-shrink-0" />
            <div className="w-full">
              <h2 className="text-2xl font-bold text-primary mb-4">10. Your Rights Under the PDPA</h2>
              <div className="text-gray-700 space-y-4">
                <p>
                  Under the Personal Data Protection Act 2010 (PDPA) of Malaysia, you have the following rights regarding your personal information:
                </p>

                <ul className="list-disc ml-6 space-y-2">
                  <li><strong>Right to Access</strong>: You may request access to the personal information we hold about you</li>
                  <li><strong>Right to Correction</strong>: You may request correction of inaccurate or incomplete personal information</li>
                  <li><strong>Right to Withdraw Consent</strong>: You may withdraw your consent for the processing of your personal information at any time, subject to legal and contractual restrictions</li>
                  <li><strong>Right to Limit Processing</strong>: You may request that we limit the processing of your personal information in certain circumstances</li>
                  <li><strong>Right to Data Portability</strong>: You may request a copy of your personal information in a structured, commonly used, and machine-readable format</li>
                </ul>

                <p className="mt-4">
                  To exercise any of these rights, please contact us using the contact information provided in Section 13 below. We will respond to your request within the timeframe required by applicable law.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Cookies and Tracking */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <Info className="h-6 w-6 text-primary mr-3 mt-1 flex-shrink-0" />
            <div className="w-full">
              <h2 className="text-2xl font-bold text-primary mb-4">11. Cookies and Tracking Technologies</h2>
              <div className="text-gray-700 space-y-4">
                <p>
                  We use cookies and similar tracking technologies to enhance your experience on our Platform, analyze usage patterns, and deliver personalized content.
                </p>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">11.1 Types of Cookies</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li><strong>Essential Cookies</strong>: Necessary for the operation of our Platform</li>
                    <li><strong>Performance Cookies</strong>: Collect information about how you use our Platform</li>
                    <li><strong>Functionality Cookies</strong>: Remember your preferences and settings</li>
                    <li><strong>Advertising Cookies</strong>: Deliver relevant advertisements (with your consent)</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">11.2 Managing Cookies</h3>
                  <p>
                    You can control and manage cookies through your browser settings. However, disabling cookies may affect the functionality of our Platform.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Policy Updates */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <RefreshCw className="h-6 w-6 text-primary mr-3 mt-1 flex-shrink-0" />
            <div className="w-full">
              <h2 className="text-2xl font-bold text-primary mb-4">12. Updates to This Privacy Policy</h2>
              <div className="text-gray-700 space-y-4">
                <p>
                  We may update this Privacy Policy from time to time to reflect changes in our practices, legal requirements, or business operations. We encourage you to review this Privacy Policy periodically.
                </p>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">12.1 Notification of Changes</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>If we make changes to this Privacy Policy, we will post the revised version on this page with an updated "Last Updated" date</li>
                    <li>If we make material changes that significantly affect your rights or how we use your personal information, we will prominently post a notice on our website</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">12.2 Continued Use</h3>
                  <p>
                    Your continued use of Tutor Founder after any changes to this Privacy Policy constitutes your acceptance of the updated Privacy Policy.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Information */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <Phone className="h-6 w-6 text-primary mr-3 mt-1 flex-shrink-0" />
            <div className="w-full">
              <h2 className="text-2xl font-bold text-primary mb-4">13. Contact Information</h2>
              <div className="text-gray-700 space-y-4">
                <p>
                  If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
                </p>

                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                  <p className="font-semibold text-primary mb-3">Tutor Founder</p>
                  <div className="space-y-2">
                    <p><strong>Address:</strong> Kuala Lumpur, Malaysia</p>
                    <p><strong>Email:</strong> <a href="mailto:tutorfounder@.com" className="text-accent hover:underline">tutorfounder@gmail.com</a></p>
                    <p><strong>Phone:</strong> <a href="tel:+601112049396" className="text-accent hover:underline">+60 11-12049396</a></p>
                  </div>
                </div>

                <p className="mt-4">
                  We will make every effort to respond to your inquiry within <strong>14 working days</strong>.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Governing Law */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <FileText className="h-6 w-6 text-primary mr-3 mt-1 flex-shrink-0" />
            <div className="w-full">
              <h2 className="text-2xl font-bold text-primary mb-4">14. Governing Law and Dispute Resolution</h2>
              <div className="text-gray-700 space-y-4">
                <p>
                  This Privacy Policy and any disputes arising out of or related to it shall be governed by and construed in accordance with the laws of Malaysia.
                </p>

                <p>
                  Any disputes shall be subject to the exclusive jurisdiction of the courts of Malaysia. Both parties agree to first attempt to resolve any disputes through good faith negotiations before resorting to formal legal proceedings.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Acknowledgment */}
        <section className="bg-primary text-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-bold mb-4">15. Acknowledgment and Consent</h2>
          <div className="space-y-4">
            <p>
              By accessing or using Tutor Founder, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy.
            </p>
            <p>
              You consent to the collection, use, disclosure, and processing of your personal information as described in this Privacy Policy.
            </p>
            <p className="font-semibold">
              If you do not agree with any part of this Privacy Policy, you must discontinue use of our Platform immediately.
            </p>
          </div>
        </section>

        {/* Footer Note */}
        <div className="text-center text-gray-600 mt-12 pt-8 border-t border-gray-200">
          <p className="text-sm">
            Thank you for trusting Tutor Founder. We are committed to protecting your privacy and providing you with the best tutoring matching experience in Malaysia.
          </p>
        </div>

      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
